<section class="mod-page hm-page" data-module="hearingManagement">
  <header class="page-header">
    <div>
      <p class="eyebrow">Hearings &amp; resolution</p>
      <h1>Hearing management</h1>
    </div>
    <div class="hm-header-stats">
      <div class="hm-stat">
        <span>Scheduled</span>
        <strong>{{ counts().scheduled }}</strong>
      </div>
      <div class="hm-stat">
        <span>Completed</span>
        <strong>{{ counts().completed }}</strong>
      </div>
      <div class="hm-stat">
        <span>Total</span>
        <strong>{{ counts().total }}</strong>
      </div>
      @if (counts().multiCase) {
        <div class="hm-stat">
          <span>Multi-hearing cases</span>
          <strong>{{ counts().multiCase }}</strong>
        </div>
      }
    </div>
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  <div class="hm-filters" role="tablist">
    @for (f of [
      { id: 'all', label: 'All' },
      { id: 'Scheduled', label: 'Scheduled' },
      { id: 'Completed', label: 'Completed' },
      { id: 'Cancelled', label: 'Cancelled' }
    ]; track f.id) {
      <button
        type="button"
        role="tab"
        [class.active]="filter() === f.id"
        (click)="setFilter($any(f.id))"
      >
        {{ f.label }}
      </button>
    }
  </div>

  <div class="hm-layout">
    <section class="panel hm-list-panel">
      <div class="hm-section-head">
        <h2>Hearing list</h2>
        <p>Each sitting is listed separately — a case can have several hearings.</p>
      </div>

      <ul class="hm-list">
        @for (h of hearings(); track h.id) {
          <li>
            <button
              type="button"
              class="hm-row"
              [class.is-active]="selected()?.id === h.id"
              (click)="select(h.id)"
            >
              <div class="hm-row-top">
                <strong>{{ h.caseNo }}</strong>
                <span [class]="'hm-pill ' + statusTone(h.status)">{{ h.status }}</span>
              </div>
              <p class="hm-row-subject">{{ h.subject }}</p>
              <div class="hm-row-meta">
                @if (seqFor(h); as seq) {
                  @if (seq.total > 1) {
                    <span class="hm-seq">Hearing {{ seq.index }} of {{ seq.total }}</span>
                  } @else {
                    <span class="hm-seq muted-seq">Single sitting</span>
                  }
                }
                <span class="hm-row-slot">{{ formatSlot(h) }} · {{ h.venue || 'Venue TBD' }}</span>
              </div>
            </button>
          </li>
        } @empty {
          <li class="hm-empty">No hearings in this filter.</li>
        }
      </ul>
    </section>

    @if (selected(); as h) {
      <section class="panel hm-detail-panel">
        <div class="hm-section-head">
          <h2>Hearing details</h2>
          @if (selectedSeq().total > 1) {
            <p>Sitting {{ selectedSeq().index }} of {{ selectedSeq().total }} for this case</p>
          }
        </div>

        <div class="hm-detail-banner">
          <div>
            <p class="hm-banner-id">{{ h.caseNo }}</p>
            <h3>{{ h.subject }}</h3>
          </div>
          <span [class]="'hm-pill ' + statusTone(h.status)">{{ h.status }}</span>
        </div>

        @if (caseHearings().length) {
          <div class="hm-timeline" aria-label="Hearings for this case">
            <div class="hm-timeline-head">
              <h4>Case hearing trail</h4>
              <span>{{ caseHearings().length }} sitting{{ caseHearings().length === 1 ? '' : 's' }}</span>
            </div>
            <ol class="hm-trail">
              @for (sibling of caseHearings(); track sibling.id; let i = $index) {
                <li>
                  <button
                    type="button"
                    class="hm-trail-item"
                    [class.is-current]="isSelected(sibling)"
                    (click)="select(sibling.id)"
                  >
                    <span class="hm-trail-num">{{ i + 1 }}</span>
                    <span class="hm-trail-body">
                      <strong>{{ formatSlot(sibling) }}</strong>
                      <em>{{ sibling.venue || 'Venue TBD' }}</em>
                    </span>
                    <span [class]="'hm-pill ' + statusTone(sibling.status)">{{ sibling.status }}</span>
                  </button>
                </li>
              }
            </ol>
          </div>
        }

        <div class="hm-detail-grid">
          <article class="hm-card accent">
            <span class="hm-label">Date</span>
            <strong>{{ formatDate(h) }}</strong>
          </article>
          <article class="hm-card accent">
            <span class="hm-label">Time slot</span>
            <strong>{{ formatTime(h) }}</strong>
          </article>
          <article class="hm-card">
            <span class="hm-label">Venue</span>
            <strong>{{ h.venue && h.venue !== '—' ? h.venue : 'To be confirmed' }}</strong>
          </article>
          <article class="hm-card">
            <span class="hm-label">Hearing officer</span>
            <strong>{{ h.officerName || h.officers || 'DM desk' }}</strong>
          </article>
        </div>

        <dl class="hm-facts">
          <div>
            <dt>Case ID</dt>
            <dd>{{ h.caseNo }}</dd>
          </div>
          <div>
            <dt>Tracking</dt>
            <dd>{{ selectedComplaint()?.trackingId || h.caseNo }}</dd>
          </div>
          <div>
            <dt>Department</dt>
            <dd>{{ selectedComplaint()?.department || '—' }}</dd>
          </div>
          <div>
            <dt>Case status</dt>
            <dd>{{ selectedComplaint()?.status || '—' }}</dd>
          </div>
          <div>
            <dt>Priority</dt>
            <dd>{{ selectedComplaint()?.priority || '—' }}</dd>
          </div>
          <div>
            <dt>This sitting</dt>
            <dd>
              @if (selectedSeq().total > 1) {
                Hearing {{ selectedSeq().index }} of {{ selectedSeq().total }}
              } @else {
                First / only sitting
              }
            </dd>
          </div>
        </dl>

        @if (selectedComplaint()?.description) {
          <div class="hm-case-blurb">
            <h4>Case summary</h4>
            <p>{{ selectedComplaint()!.description }}</p>
          </div>
        }

        @if (canConduct() && h.status === 'Scheduled') {
          <label class="hm-notes">
            Hearing notes
            <textarea
              class="field"
              rows="4"
              [ngModel]="notes()"
              (ngModelChange)="notes.set($event)"
              placeholder="Record observations from this sitting…"
            ></textarea>
          </label>

          <div class="hm-actions">
            <button type="button" class="btn primary" [disabled]="busy()" (click)="markHeard()">
              {{ busy() ? 'Saving…' : 'Hearing Conducted' }}
            </button>
            <button type="button" class="btn" [disabled]="busy()" (click)="cancel()">Cancel hearing</button>
            <a routerLink="/scheduling" [queryParams]="{ case: h.caseNo }" class="btn ghost">
              Add another sitting
            </a>
            <a routerLink="/resolution" [queryParams]="{ case: h.caseNo }" class="btn ghost">
              Open resolution desk
            </a>
          </div>
        } @else if (h.status === 'Completed') {
          <div class="hm-done-banner">
            <span class="hm-pill tone-ready">Completed</span>
            This sitting is done.
            @if (hasOpenSibling()) {
              Other sittings for this case are still open — pick one from the trail above.
            } @else {
              Issue the final order, or request another hearing if needed.
            }
          </div>
          <div class="hm-actions">
            <button type="button" class="btn primary" disabled>Completed</button>
            @if (canConduct() && !hasOpenSibling()) {
              <button type="button" class="btn" [disabled]="busy()" (click)="requestAnotherHearing()">
                {{ busy() ? 'Sending…' : 'Need another hearing' }}
              </button>
            }
            <a routerLink="/scheduling" [queryParams]="{ case: h.caseNo }" class="btn ghost">
              Schedule desk
            </a>
            <a routerLink="/resolution" [queryParams]="{ case: h.caseNo }" class="btn">Go to Resolution</a>
          </div>
        } @else if (h.status === 'Cancelled') {
          <div class="hm-done-banner hm-done-cancelled">This sitting was cancelled.</div>
          <div class="hm-actions">
            <a routerLink="/scheduling" [queryParams]="{ case: h.caseNo }" class="btn primary">
              Book a new sitting
            </a>
            @if (canConduct() && selectedComplaint()?.status === 'heard') {
              <button type="button" class="btn" [disabled]="busy()" (click)="requestAnotherHearing()">
                {{ busy() ? 'Sending…' : 'Need another hearing' }}
              </button>
            }
          </div>
        } @else {
          <p class="muted" style="margin-top: 0.75rem">No conduct actions for this status.</p>
        }
      </section>
    } @else {
      <section class="panel hm-detail-panel hm-detail-empty">
        <p class="muted">No hearing selected. Bookings from Peshkar appear here for DM.</p>
      </section>
    }
  </div>
</section>
